import React, { useState, useEffect } from 'react'
import { Card, Button, Grid, Typography } from '@material-ui/core'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogContentText from '@material-ui/core/DialogContentText'
import Chip from '@material-ui/core/Chip'
import CircularProgress from '@material-ui/core/CircularProgress'
import { useCookies } from 'react-cookie'
import { useTranslation } from 'react-i18next'
import lodash from 'lodash'
import useGlobal, { GlobalState, GlobalActions } from '../../../Store'
import { formatCents } from '../../../Utilities'
import {
  REFRESH_TOKEN_COOKIE_NAME,
  JWT_COOKIE_NAME,
  LOGIN_LINK,
} from '../../../Constants'
import {
  RequestSectionProposalFragment,
  useRequestSectionMutationMutation,
} from './RequestSection.generated-types'
import { DialogTitle } from '../../../Components/UI/Dialog/DialogTitle'
import { s, ScollToBreakdown } from './style'
import { Box } from '@mui/material'

const PROPOSAL_BUTTONS_ROOT_STYLE: React.CSSProperties = {
  marginTop: '32px',
  fontWeight: 600,
  textTransform: 'none',
  width: '100%',
}

interface RequestSectionProps {
  proposal: RequestSectionProposalFragment
  goToConfirmation: () => void
  inactive?: boolean
  isRequestedToBooked?: boolean
  canEdit: boolean
  editing: boolean
  handleStartEditing: () => void
  handleUpdateProposal: () => void
  updatingProposal: boolean
  proposalUpdated: boolean
}

export const RequestSection: React.FC<RequestSectionProps> = ({
  proposal,
  proposal: { ID, baseCostTotal, extraCostItems, currency },
  goToConfirmation,
  inactive,
  isRequestedToBooked,
  canEdit,
  editing,
  handleStartEditing,
  handleUpdateProposal,
  updatingProposal,
  proposalUpdated,
}) => {
  const { t } = useTranslation()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const costItemsByIDs = lodash.keyBy(extraCostItems, 'ID')
  const [cookies] = useCookies([REFRESH_TOKEN_COOKIE_NAME, JWT_COOKIE_NAME])
  const [{ fetching, error }, requestToBook] =
    useRequestSectionMutationMutation()
  const [globalState] = useGlobal<GlobalState, GlobalActions>()
  const [total, setTotal] = useState(0)

  const isNotLoggedIn =
    !cookies[REFRESH_TOKEN_COOKIE_NAME] && !cookies[JWT_COOKIE_NAME]
  const currentUrl = window.location.href

  const handleViewModal = (): void => {
    setIsModalVisible(true)
  }

  const handleCloseModal = (): void => {
    setIsModalVisible(false)
  }

  const handleScrollToBreakDown = (): void => {
    const element = document.getElementById('costBreakdown')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handleRequestToBook = async (): Promise<void> => {
    if (isNotLoggedIn) {
      window.location.assign(`${LOGIN_LINK}?redirect_to=${currentUrl}`)
    } else {
      const result = await requestToBook({
        input: {
          proposalID: ID,
          costItems: Object.values(globalState[ID]?.bookedItems || []),
        },
      })

      setIsModalVisible(false)

      if (!result.error) {
        goToConfirmation()
      }
    }
  }

  useEffect(() => {
    const total = lodash.reduce(
      globalState[ID]?.bookedItems,
      (total, bookedItem) => {
        const costPerUnit = costItemsByIDs[bookedItem.ID]?.costPerUnit || 0
        return total + costPerUnit * bookedItem.quantity
      },
      0
    )
    setTotal(total)
  }, [globalState[ID]])

  return (
    <Card style={s.requestSectionCard}>
      <Grid container style={s.totalSection}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" style={s.costTotal}>
            {'Total cost'}
          </Typography>
          <Typography variant="h3" style={s.amount}>
            {formatCents(currency, baseCostTotal + total)}
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#1F1F1F',
            }}>{` (${currency === 'AUD' ? 'inc GST' : 'ex VAT'})`}</span>
          </Typography>
        </Grid>
        <Grid item xs={12} md={6} className="scrollToBreakdown">
          <ScollToBreakdown>
            <Button
              style={s.scollToBreakdown}
              size="small"
              onClick={handleScrollToBreakDown}
            >
              See cost breakdown
            </Button>
          </ScollToBreakdown>
        </Grid>
      </Grid>
      <Grid container>
        <Grid item xs={12}>
          {(() => {
            if (canEdit) {
              if (updatingProposal) {
                return (
                  <Box
                    sx={{
                      width: '100%',
                      marginTop: '32px',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <CircularProgress />
                  </Box>
                )
              }

              if (editing) {
                return (
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={inactive}
                    onClick={handleUpdateProposal}
                    style={PROPOSAL_BUTTONS_ROOT_STYLE}
                  >
                    Update proposal
                  </Button>
                )
              }

              return (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={inactive}
                  onClick={handleStartEditing}
                  style={{
                    ...PROPOSAL_BUTTONS_ROOT_STYLE,
                    ...(proposalUpdated ? { background: 'green' } : {}),
                  }}
                >
                  {proposalUpdated ? 'Proposal updated' : 'Edit proposal'}
                </Button>
              )
            }

            return null
          })()}
          <Button
            variant="contained"
            color="primary"
            style={PROPOSAL_BUTTONS_ROOT_STYLE}
            onClick={handleViewModal}
            disabled={inactive}
          >
            {isRequestedToBooked
              ? t('proposal.proposalButtons.title', 'Requested to book')
              : t('proposal.proposalButtons.title', 'Request to book')}
          </Button>

          <Dialog open={isModalVisible} onClose={handleCloseModal}>
            <DialogTitle id="requestDialogTitle" onClose={handleCloseModal}>
              {t('proposal.proposalButtons.modalTitle', 'Confirm request?')}
            </DialogTitle>
            <DialogContent>
              <DialogContentText id="requestDialogDescription">
                {fetching ? (
                  <CircularProgress />
                ) : (
                  t(
                    'proposal.proposalButtons.modalWarning',
                    'Ensure all details are correct as this request will form the basis of your contract.'
                  )
                )}
                {error && (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={'Oops... something went wrong'}
                    color="primary"
                    style={s.errorChip}
                  />
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button
                variant="outlined"
                onClick={handleCloseModal}
                style={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={async () => await handleRequestToBook()}
                style={{ textTransform: 'none' }}
                color="primary"
                autoFocus
              >
                {t('proposal.proposalButtons.modalOk', 'Confirm request')}
              </Button>
            </DialogActions>
          </Dialog>
        </Grid>
      </Grid>
    </Card>
  )
}
